import isExternalImage from '@ahd/shared/helpers/ahd-fleet/is-external-image';
import stringToSlug from '@ahd/shared/tools/string-to-slug';
import type { PrintContext } from '@ahd/shared/types/ahd-cars-print/context';
import type { Car } from '@ahd/shared/types/ahd-cars/car/car';
import type { CarImage } from '@ahd/shared/types/ahd-cars/car/image';
import TreeItem from '@mui/lab/TreeItem';
import { type AxiosProgressEvent } from 'axios';
import { formatDuration } from 'date-fns/formatDuration';
import move from 'lodash-move';
import type { Dispatch, SetStateAction } from 'react';
import { type NavigateFunction, type NavigateOptions } from 'react-router-dom';
import sAkte from '../_json/s_akte';
import { getPrint as getPrintAxios } from './AxiosPrint';
import { uploadAkte, urlImagesRelease } from './AxiosVars';
import { formatDate } from './Date';
import { pushHistory } from './History';
import { isDebug } from './Session';
import { getFieldFromObject } from './Texte';
import { formatDataGridValue } from './datagrid/func';
import { type DataGridProps } from './datagrid/types';

export type FileTypeAccept = {
    'application/csv'?: '.csv'[];
    'application/pdf'?: '.pdf'[];
    'application/zip'?: '.zip'[];
    'image/jpg'?: ('.jpeg' | '.jpg')[];
    'image/png'?: '.png'[];
    'image/tiff'?: ('.tif' | '.tiff')[];
};

export const round = function (number: number, decimals: number): number {
    if (decimals <= 0) {
        return number;
    }

    const factor = decimals > 0 ? 10 ^ decimals : 1;
    return Math.round((number + Number.EPSILON) * factor) / factor;
};

export const neededTableColumnDefinitionForUserSettings = (defaultShow: boolean) => [
    { id: 'field', default: '' },
    { id: 'order', default: 'index' },
    { id: 'pinned', default: false },
    { id: 'show', default: defaultShow || false },
    { id: 'sort', default: false },
    { id: 'width', default: undefined },
];

export function checkUndefined(e: unknown): e is undefined {
    return e === undefined || e === 'undefined';
}

function checkNull(e: unknown): e is null {
    return e === null || e === 'null';
}

type Empty = [] | Record<string, never> | '' | '""';
function checkEmpty(e: unknown): e is Empty {
    if (Array.isArray(e) && e.length < 1) return true;
    else if (typeof e === 'object' && Object.keys(e).length < 1) return true;
    else if (e === '' || e === '""') return true;
    return false;
}

export function checkUndefinedOrNull(e: unknown): e is undefined | undefined {
    return checkUndefined(e) || checkNull(e);
}

export function checkUndefinedNullOrEmpty(e: unknown): e is undefined | undefined | Empty {
    return checkUndefined(e) || checkNull(e) || checkEmpty(e);
}

export function isLocalServer() {
    return !checkUndefinedNullOrEmpty(location.port);
}

export function isDevBeta() {
    return location.host.startsWith('cars-dev.') || location.host.startsWith('cars-next.');
}

export const availableImageSizes = [100, 134, 320, 360, 480, 520, 640, 1024, 1280, 1600, 1920, 2048];

export function imageLoader({ src: origSrc, width }: { src: string; width?: number }) {
    const srcSplit = origSrc?.split('/') || [];
    const rawPossibleTs = srcSplit[srcSplit.length - 1]?.match(/\?.*/);
    const possibleTs = !checkUndefinedNullOrEmpty(rawPossibleTs) ? rawPossibleTs[0] : '';
    const possibleWidth = parseInt(srcSplit[srcSplit.length - 1]);
    const hasWidthNumber = !isNaN(possibleWidth) && possibleWidth > 0;

    if (hasWidthNumber && !checkUndefinedNullOrEmpty(width)) {
        if (!checkUndefinedNullOrEmpty(origSrc) && !origSrc.endsWith('/placeholder.svg')) {
            const cuttedSrc = origSrc.substring(0, origSrc.lastIndexOf('/'));
            return cuttedSrc + '/' + width + possibleTs;
        } else {
            return '/media/placeholder.svg';
        }
    }

    return origSrc;
}

export function getDecimals(value: number): string {
    if (Math.floor(value) !== value) {
        return value?.toString().split('.')[1];
    }
    return '';
}

export type BetterNavigateOptions = NavigateOptions & {
    pathname?: string;
};

export function betterUseNavigate(navigate: NavigateFunction, locState: any, options: BetterNavigateOptions, unchangedData: boolean) {
    if (typeof navigate !== 'function') {
        console.error('Navigate ist keine Funktion!');
        return false;
    }
    if (checkUndefinedNullOrEmpty(navigate)) {
        console.error('Navigate ist leer!');
        return false;
    }
    if (checkUndefinedNullOrEmpty(options.replace)) {
        console.error('Replace ist nicht definiert!');
        return false;
    }
    if (checkUndefinedNullOrEmpty(unchangedData)) {
        console.error('UnchangedData ist leer!');
        return false;
    }

    const newPathname = unchangedData === true ? undefined : options.pathname || undefined;
    const newOptions = {
        replace: unchangedData === true || options.replace,
        state: {
            ...(locState || {}),
            ...(unchangedData === true ? { unchangedNavigateOptions: options } : options.state),
        },
    };

    pushHistory({ pathname: newPathname, ...newOptions });
    navigate(newPathname, newOptions);
    return true;
}

export function encodeDecodeURIComponent(text, decode = false) {
    if (checkUndefinedNullOrEmpty(text)) return text;
    try {
        if (decode) return decodeURIComponent(text);
        else {
            return encodeURIComponent(text);
        }
    } catch {
        return text;
    }
}

export function generateRandomId() {
    let result = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    for (let i = 0; i < 12; i++) result += characters.charAt(Math.floor(Math.random() * characters.length));
    return result;
}

export type UpdateObject = {
    id: string;
    val: any;
    container?: string;
};

export function updateState<FormData, FormError>(
    e: UpdateObject | UpdateObject[],
    formData: FormData,
    setFormData?: Dispatch<SetStateAction<FormData>>,
    formError?: FormError,
    setFormError?: Dispatch<SetStateAction<FormError>>
) {
    const newData: FormData = Object.assign({}, formData);
    const newError: FormError = Object.assign({}, formError);
    if (Array.isArray(e)) {
        for (let i = 0; i < e.length; i++) {
            if (e[i].container) {
                if (checkUndefinedOrNull(newData[e[i].container])) newData[e[i].container] = {};
                newData[e[i].container][e[i].id] = e[i].val;
                if (!checkUndefined(newError[e[i].container]) && !checkUndefined(newError[e[i].container][e[i].id])) delete newError[e[i].container][e[i].id];
            } else {
                newData[e[i].id] = e[i].val;
                if (!checkUndefined(newError[e[i].id])) delete newError[e[i].id];
            }
        }
    } else {
        if (e.container) {
            if (checkUndefinedOrNull(newData[e.container])) newData[e.container] = {};
            newData[e.container][e.id] = e.val;
            if (!checkUndefined(newError[e.container]) && !checkUndefined(newError[e.container][e.id])) delete newError[e.container][e.id];
        } else {
            newData[e.id] = e.val;
            if (!checkUndefined(newError[e.id])) delete newError[e.id];
        }
    }

    if (!checkUndefinedNullOrEmpty(setFormData)) setFormData(newData);
    if (!checkUndefinedNullOrEmpty(setFormError)) setFormError(newError);

    return { formData: newData, formError: newError };
}

export function delay(ms: number) {
    return new Promise(function (rs) {
        setTimeout(rs, ms);
    });
}

export function chunkArray(array: any[], size: number) {
    const chunkedArr = [];
    for (let i = 0; i < array.length; i++) {
        const last = chunkedArr[chunkedArr.length - 1];
        if (!last || last.length === size) chunkedArr.push([array[i]]);
        else last.push(array[i]);
    }
    return chunkedArr;
}

export function mergeArrays(...arrays: any[]) {
    const mergedArr: any[] = [];
    for (let i = 0; i < arrays.length; i++) {
        if (Array.isArray(arrays[i])) {
            arrays[i].map(function (val: any) {
                if (!mergedArr.includes(val)) {
                    mergedArr.push(val);
                }
            });
        }
    }
    return mergedArr;
}

export const moveItemInArray = (input: any[], from: number, to: number) => {
    return move(input, from, to);
};

export const uniqueArray = (array: any[], unifyBy?: string) => {
    const returnArr = array.filter(function (value, index, self) {
        if (!checkUndefinedNullOrEmpty(unifyBy) && typeof value === 'object') {
            return index === self.findIndex((t) => t[unifyBy] === value[unifyBy]);
        }

        return self.indexOf(value) === index;
    });
    return returnArr;
};

export function findDuplicatesInArray(arr: any[]) {
    const sortedArr = arr.slice().sort(); // You can define the comparing function here.
    // JS by default uses a crappy string compare.
    // (we use slice to clone the array so the
    // original array won't be modified)
    const results = [];
    for (let i = 0; i < sortedArr.length - 1; i++) {
        if (sortedArr[i + 1] == sortedArr[i]) {
            results.push(sortedArr[i]);
        }
    }
    return results;
}

export function decodeHTMLEntities(text: string) {
    if (checkUndefinedNullOrEmpty(text)) return text;
    const list = [
        ['amp', '&'],
        ['#x26', '&'],
        ['apos', "'"],
        ['#x27', "'"],
        ['#x2F', '/'],
        ['#47', '/'],
        ['lt', '<'],
        ['#x3C', '<'],
        ['gt', '>'],
        ['#x3E', '>'],
        ['nbsp', ' '],
        ['#xA0', ' '],
        ['#13', ''],
        ['quot', '"'],
        ['#x22', '"'],
        ['Auml', 'Ä'],
        ['#xC4', 'Ä'],
        ['#xE4', 'ä'],
        ['auml', 'ä'],
        ['#xD6', 'Ö'],
        ['Ouml', 'Ö'],
        ['#xF6', 'ö'],
        ['ouml', 'ö'],
        ['#xDC', 'Ü'],
        ['Uuml', 'Ü'],
        ['#xFC', 'ü'],
        ['uuml', 'ü'],
        ['#xDF', 'ß'],
        ['szlig', 'ß'],
    ];
    for (let i = 0; i < list.length; i++) {
        text = text.replace(new RegExp('&' + list[i][0] + ';', 'g'), list[i][1]);
    }
    return text;
}

export function putValueBackInRow(rowData, field, value) {
    const fieldArray = field && field.split('.');

    if (!checkUndefinedNullOrEmpty(fieldArray) && fieldArray.length === 3) {
        if (checkUndefinedNullOrEmpty(rowData[fieldArray[0]])) {
            rowData[fieldArray[0]] = {};
        }
        if (checkUndefinedNullOrEmpty(rowData[fieldArray[0]][fieldArray[1]])) {
            rowData[fieldArray[0]][fieldArray[1]] = {};
        }
        rowData[fieldArray[0]][fieldArray[1]][fieldArray[2]] = value;
    }
    if (!checkUndefinedNullOrEmpty(fieldArray) && fieldArray.length === 2) {
        if (checkUndefinedNullOrEmpty(rowData[fieldArray[0]])) {
            rowData[fieldArray[0]] = {};
        }
        rowData[fieldArray[0]][fieldArray[1]] = value;
    }
    if (!checkUndefinedNullOrEmpty(fieldArray) && fieldArray.length === 1) {
        rowData[fieldArray[0]] = value;
    }
    if (checkUndefinedNullOrEmpty(fieldArray)) {
        rowData = value;
    }
}

export function generateCSV(title: string, columns: DataGridProps['columns'], data: DataGridProps['rows'], selectBy?, selected?) {
    let csvContent = 'data:text/csv;charset=utf-8,';

    // Header
    const arrayHead = [];
    columns.map((col) => !col.hidden && arrayHead.push('"' + col.headerName + '"'));
    csvContent += arrayHead.join(';') + '\r\n';

    // Data
    let arrayData = Object.assign([], data);
    if (!checkUndefinedNullOrEmpty(selected)) {
        arrayData = [];
        for (let i = 0; i < data.length; i++) {
            if (selected.includes(data[i][selectBy])) arrayData.push(data[i]);
        }
    }
    arrayData.forEach((element) => {
        const arrayContent = [];
        columns.map((col) => {
            if (!col.hidden) {
                const val = formatDataGridValue(undefined, col, getFieldFromObject(element, col.field));
                arrayContent.push(`"${!checkUndefinedNullOrEmpty(val) ? val?.toString()?.replace('"', "'") : '-'}"`);
            }
        });
        csvContent += decodeHTMLEntities(arrayContent.join(';') + '\r\n');
    });
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', title + '.csv');
    link.click();
}

export function getImageFromArray(car: Pick<Car, 'imgs' | 'se'>, typ: number, nr?: number, notNr0 = false, errors = false): CarImage {
    if (checkUndefinedOrNull(car)) {
        if (errors) console.error('Car ist leer!');
        return undefined;
    }
    if (checkUndefinedOrNull(car?.imgs)) {
        if (errors) console.error('Car-Images ist leer!');
        return undefined;
    }
    if (!Array.isArray(car?.imgs)) {
        if (errors) console.error('Car-Images ist kein Array!');
        return undefined;
    }
    if (checkUndefinedOrNull(typ)) {
        if (errors) console.error('Typ ist leer!');
        return undefined;
    }
    if (!car || !car?.imgs || checkUndefined(typ)) return undefined;
    for (let i = 0; i < car?.imgs.length; i++) {
        const image = car?.imgs[i];
        if (image?.typ === typ && (!notNr0 || (notNr0 && image?.nr !== 0)) && (!nr || image?.nr === nr)) {
            if (!isExternalImage(image?.uThumb)) {
                image.uThumb = urlImagesRelease + '/s/v1/imgc/' + car?.se?.i + '/' + image?.id + '/640?ts=' + image?.c;
            }
            if ([20].includes(image?.typ)) {
                image.url = urlImagesRelease + '/s/v1/' + image?.id + '/orig?ts=' + image?.c;
            } else {
                image.url = urlImagesRelease + '/s/v1/imgc/' + car?.se?.i + '/' + image?.id + '/1600?ts=' + image?.c;
            }
            return image;
        }
    }
    return undefined;
}

export function getCarThumbnail(car: Pick<Car, 'deleted' | 'dv' | 'imgs' | 'se'>, location: Location, ecType: number): CarImage {
    const empty = { url: 'undefined' };

    if (!car || !location || checkUndefined(ecType)) return empty;

    const verkauft = { url: '/images/car_verkauft.png' };
    const placeholder = getImageFromArray(car, 7);
    const eyecatcher = getImageFromArray(car, 0, 0);
    const firstImage = getImageFromArray(car, 0);

    if (car?.deleted) return empty;
    else if (car?.dv > 0 && !location.href.includes('/vehicles')) return verkauft;
    else if (!checkUndefinedNullOrEmpty(car?.imgs)) {
        if (ecType !== 0 && eyecatcher) return eyecatcher;
        else if (firstImage) return firstImage;
        else if (placeholder) return placeholder;
        else return empty;
    } else return empty;
}

export async function getPrint(
    id: PrintContext['id'],
    cars: PrintContext['cars'],
    params: PrintContext['params'],
    callbackUpdate: (params: NetworkProgress) => void,
    signal: AbortController['signal']
): Promise<{ data: string; file: File }> {
    const { data, file } = await getPrintAxios(id, cars, params, callbackUpdate, signal);

    try {
        if (!isDebug()) {
            const idFahrzeug = cars?.[0]?.id;
            const akte = sAkte?.find((a) => a?.printIds?.includes(id));
            if (!checkUndefinedNullOrEmpty(idFahrzeug) && !checkUndefinedNullOrEmpty(akte?.i)) {
                const akteName = `${stringToSlug(akte?.t)}-${formatDate(new Date(), 'yyyy-MM-dd-HH-mm-ss')}.pdf`;
                await uploadAkte(idFahrzeug, akte?.i, file, akteName, undefined);
            }
        }
    } catch (err) {
        console.error(err);
    }

    return { data, file };
}

export function toBase64(file): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = (error) => reject(error);
    });
}

export function getBestRate(value: number | '-', faktor: number, einheiten: string[] = []): string {
    if (!value) return undefined;
    let returnValue = value;
    let einheitIndex = 0;

    while (returnValue !== '-' && returnValue / faktor >= 1) {
        returnValue = Math.round((returnValue / faktor) * 100) / 100;
        einheitIndex++;
    }

    return [returnValue, einheiten[einheitIndex]].join(' ');
}

export type NetworkProgress = {
    /** An error occured. */
    error?: string;
    /** Estimated time in seconds. */
    estimated?: string;
    /** Amount of already loaded data. */
    loaded?: string;
    /** Mode of network movement. */
    mode?: 'upload' | 'download';
    /** Progress in percent. */
    progress?: number;
    /** Speed of network activity. */
    rate?: string;
    /** Amount of whole data. */
    total?: string;
};

/**
 * @param {Object} progressEvent - Progress-Object von Axios
 * @param {Function} downloadProgressFunc - Return-Funktion für Axios-Aufruf
 */
export function networkHandler(progressEvent: AxiosProgressEvent, downloadProgressFunc: (param: NetworkProgress) => void) {
    if (!checkUndefinedOrNull(downloadProgressFunc)) {
        const estimated = formatDuration({ seconds: Math.round(progressEvent?.estimated) });
        const loaded = getBestRate(progressEvent?.loaded, 1024, ['B', 'KB', 'MB', 'GB', 'TB', 'PB']);
        const mode = progressEvent?.upload === true ? 'upload' : 'download';
        const progress = !checkUndefinedNullOrEmpty(progressEvent?.progress) && !isNaN(progressEvent?.progress) ? Math.round(progressEvent?.progress * 10000) / 100 : undefined;
        const rate = getBestRate(progressEvent?.rate, 1000, ['bit/s', 'Kbit/s', 'Mbit/s', 'Gbit/s']);
        const total = getBestRate(progressEvent?.total, 1024, ['B', 'KB', 'MB', 'GB', 'TB', 'PB']);

        downloadProgressFunc({
            estimated,
            loaded,
            mode,
            progress,
            rate,
            total,
        });
    }
}

export function parseJson<T = Record<string, any>>(string: string): T {
    try {
        if (checkUndefinedNullOrEmpty(string)) {
            return undefined;
        }
        const o = JSON.parse(string);
        if (o && typeof o === 'object') {
            return o;
        }
    } catch {
        return undefined;
    }
}

export function stringifyJson<T = Record<string, any>>(object: T): string {
    try {
        if (checkUndefinedNullOrEmpty(object)) {
            return undefined;
        }
        const s = JSON.stringify(object);
        if (s && typeof s === 'string') {
            return s;
        }
    } catch (e) {
        console.error(e);
        return undefined;
    }
}

export function generateTreeData(data: Record<string, unknown> | string[], id: string) {
    if (checkUndefinedNullOrEmpty(data)) {
        return {
            defaultExpanded: [],
            viewData: [],
        };
    }

    const arr = [];
    const arrKeys = Object.keys(data);
    const ignoreKeys = ['domContainerId'];

    arrKeys.map(function (key) {
        if (!ignoreKeys.includes(key) && !checkUndefined(data[key])) {
            const isNull = checkUndefinedOrNull(data[key]);
            const isObject = typeof data[key] === 'object';
            const content = isObject ? generateTreeData(data[key], id + '_' + key) : undefined;

            arr.push(
                <TreeItem key={'tree_' + id + '_' + key} nodeId={'tree_' + id + '_' + key} label={isNull ? key + ': undefined' : isObject ? key : key + ': ' + data[key]}>
                    {isNull ? 'undefined' : isObject ? content.viewData : undefined}
                </TreeItem>
            );
        }
    });

    return {
        defaultExpanded: arrKeys.reduce((prev, curr) => {
            return [...prev, 'tree_' + id + '_' + curr];
        }, []),
        viewData: arr,
    };
}
